import { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet } from 'react-native';
import type { ViewToken } from 'react-native';
import {
  Flex,
  List,
  RemoteImage,
  Typography,
  IconChevronRight,
  type ListRef,
} from '@banhala/ui-kit';
import type { BannerCarouselProps, BannerCarouselItem } from './types';
import type { AnyObject } from '../../../../types';
import { isNil, isNotEmpty } from '../../../../utils';
import { useOpenDeeplink, useWebDragToScroll } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';

const BANNER_WIDTH = 240;
const BANNER_HEIGHT = 134;
const BANNER_BORDER_RADIUS = 4;
const MORE_BUTTON_SIZE = 48;

type BannerEntity = { item: BannerCarouselItem; logging: AnyObject };

interface BannerItemProps {
  item: BannerCarouselItem;
  logging: AnyObject;
}

const BannerItem = ({ item, logging }: BannerItemProps) => {
  const { thumbnail, deeplink } = item;
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const handlePress = useCallback(() => {
    if (deeplink) {
      logAnalyticsEvent({
        name: 'CLICK_EVENT',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      openDeeplink(deeplink);
    }
  }, [openDeeplink, deeplink, logging, logAnalyticsEvent]);

  return (
    <Flex
      width={BANNER_WIDTH}
      height={BANNER_HEIGHT}
      borderRadius={BANNER_BORDER_RADIUS}
      overflow="hidden"
      onPress={handlePress}
    >
      <RemoteImage
        uri={thumbnail}
        width={BANNER_WIDTH}
        height={BANNER_HEIGHT}
      />
    </Flex>
  );
};

interface MoreButtonProps {
  onPress: () => void;
}

const MoreButton = ({ onPress }: MoreButtonProps) => (
  <Flex
    width={96}
    height={BANNER_HEIGHT}
    alignItems="center"
    justifyContent="center"
    onPress={onPress}
  >
    <Flex
      width={MORE_BUTTON_SIZE}
      height={MORE_BUTTON_SIZE}
      borderRadius={MORE_BUTTON_SIZE / 2}
      borderWidth={1}
      borderColor="border_primary"
      alignItems="center"
      justifyContent="center"
    >
      <IconChevronRight width={24} height={24} color="content_secondary" />
    </Flex>
    <Flex padding={{ top: 8 }}>
      <Typography type="body3" color="content_secondary">
        더보기
      </Typography>
    </Flex>
  </Flex>
);

const keyExtractor = (_item: BannerEntity, index: number) => index.toString();

const renderItem = ({ item }: { item: BannerEntity }) => (
  <BannerItem item={item.item} logging={item.logging} />
);

// 배너 50% 노출 판정.
const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

export const BannerCarousel = ({
  entity,
  body,
  isVisible,
  isAwaitingDataChange,
}: BannerCarouselProps) => {
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const listRef = useRef<ListRef<BannerEntity>>(null);
  useWebDragToScroll(listRef);

  // 배너별 DISPLAY_EVENT 중복 발화 방지(index 기준).
  const displayedRef = useRef(new Set<number>());
  const lastViewableItemsRef = useRef<ViewToken[]>([]);
  // onViewableItemsChanged 는 FlatList 가 런타임 교체를 허용하지 않으므로
  // 게이트 값은 ref 로 읽어 콜백 정체성을 안정적으로 유지한다.
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;
  const isAwaitingDataChangeRef = useRef(isAwaitingDataChange);
  isAwaitingDataChangeRef.current = isAwaitingDataChange;

  // 게이트: 부모가 50% 가시(`isVisible`)이고 데이터 교체 윈도우가 아닐 때만 emit.
  // 가로 List는 mount 즉시 inner viewable을 보고하므로, 부모 세로 viewport
  // 밖에서의 조기 발화를 차단한다. (CLICK_EVENT와 동일 채널·파라미터)
  const emitForViewable = useCallback(
    (viewableItems: ViewToken[]) => {
      if (!isVisibleRef.current) return;
      if (isAwaitingDataChangeRef.current === true) return;
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index === null || !isViewable) return;
        if (displayedRef.current.has(index)) return;
        displayedRef.current.add(index);
        const banner = item as BannerEntity;
        logAnalyticsEvent({
          name: 'DISPLAY_EVENT',
          analytics: banner.logging.analytics,
          ably_only_log: banner.logging.ably_only_log,
        });
      });
    },
    [logAnalyticsEvent],
  );

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      lastViewableItemsRef.current = viewableItems;
      emitForViewable(viewableItems);
    },
    [emitForViewable],
  );

  // 게이트가 열리는 순간(가시 전환/awaiting 해제) 캐시된 viewable로 재발화.
  useEffect(() => {
    if (!isVisible || isAwaitingDataChange) return;
    emitForViewable(lastViewableItemsRef.current);
  }, [isVisible, isAwaitingDataChange, emitForViewable]);

  const handleMorePress = useCallback(() => {
    const deeplink = body?.item?.deeplink;
    if (deeplink) {
      logAnalyticsEvent({
        name: 'CLICK_EVENT_MORE',
        analytics: body.logging.analytics,
        ably_only_log: body.logging.ably_only_log,
      });
      openDeeplink(deeplink);
    }
  }, [openDeeplink, body, logAnalyticsEvent]);

  const showMoreButton = isNotEmpty(body?.item?.deeplink);

  const ListFooter = useMemo(() => {
    if (!showMoreButton) return null;
    return <MoreButton onPress={handleMorePress} />;
  }, [showMoreButton, handleMorePress]);

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List<BannerEntity>
      ref={listRef}
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      ListFooterComponent={ListFooter}
      ListFooterComponentStyle={styles.footer}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 16,
    gap: 12,
    alignItems: 'center',
  },
  footer: {
    justifyContent: 'center',
  },
});
